import axios from 'axios';
import i18n from '../i18n/config';

const apiBaseUrl =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

const api = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

function isTokenValid(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

const isAdminPage = () => window.location.pathname.startsWith('/admin');
const isDriverPage = () => window.location.pathname.startsWith('/driver');

const isAdminApiUrl = (url) =>
  url?.includes('/api/admin/') ||
  url?.includes('/api/orders') ||
  url?.includes('/api/carousels') ||
  (url?.includes('/api/products') && !url?.includes('/like'));

const isDriverApiUrl = (url) => url?.includes('/api/driver');

const isCustomerRequest = (config) => config.url?.includes('/api/customer/');
const isPaymentRequest = (config) => config.url?.includes('/api/payment/');

api.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      // Three independent JWT storage namespaces, by design:
      //   - `token`         → admin (User with ROLE_ADMIN)
      //   - `driver_token`  → driver (User with ROLE_DRIVER, separate
      //                       so a driver login in tab B doesn't kick
      //                       the admin out of tab A)
      //   - `customer_token`→ customer (Client entity)
      //
      // Routing rules below pick the right one. Driver pages/URLs
      // prefer the driver token; an admin happens to be able to call
      // /api/driver/* via role hierarchy, so we fall back to the admin
      // token if the driver one isn't present.
      if (isDriverApiUrl(config.url) || isDriverPage()) {
        const driverToken = localStorage.getItem('driver_token');
        const adminToken = localStorage.getItem('token');
        if (isTokenValid(driverToken)) {
          config.headers.Authorization = `Bearer ${driverToken}`;
        } else if (isTokenValid(adminToken)) {
          config.headers.Authorization = `Bearer ${adminToken}`;
        } else {
          if (driverToken) localStorage.removeItem('driver_token');
          if (adminToken) localStorage.removeItem('token');
        }
      } else if (isAdminPage() && isAdminApiUrl(config.url)) {
        const token = localStorage.getItem('token');
        if (isTokenValid(token)) {
          config.headers.Authorization = `Bearer ${token}`;
        } else if (token) {
          localStorage.removeItem('token');
        }
      } else if (isCustomerRequest(config) || isPaymentRequest(config)) {
        const customerToken = localStorage.getItem('customer_token');
        if (isTokenValid(customerToken)) {
          config.headers.Authorization = `Bearer ${customerToken}`;
        }
      }
    }

    // Forward the per-order signed payment token (issued at order creation)
    // so guest checkout can confirm payments without a real customer JWT.
    // Key MUST match Checkout.js's `sessionStorage.setItem('paymentToken', ...)`
    // (camelCase). A previous regression had this read `payment_token` and
    // every guest COD/Stripe/PayPal confirm silently 403'd — no auth header
    // was attached.
    if (isPaymentRequest(config) && !config.headers['X-Payment-Token']) {
      const paymentToken = sessionStorage.getItem('paymentToken');
      if (paymentToken) {
        config.headers['X-Payment-Token'] = paymentToken;
      }
    }

    const lang = i18n.language || localStorage.getItem('i18nextLng') || 'sq';
    if (config.method === 'get' && !config.url.includes('/admin/')) {
      config.params = config.params || {};
      config.params.lang = lang;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    // Treat 401 (no/invalid JWT) and 403 (valid JWT, wrong role) the
    // same way for admin/driver flows. We previously only handled 401
    // and a 403 — produced when e.g. a driver's JWT was wrongly
    // attached to an /api/admin/* call — would silently 'succeed'
    // with empty data and leave the operator on a broken page.
    if (status === 401 || status === 403) {
      if (isDriverPage() || isDriverApiUrl(error.config?.url)) {
        localStorage.removeItem('driver_token');
        if (isDriverPage()) {
          window.location.href = '/driver/login';
        }
      } else if (isAdminPage() && isAdminApiUrl(error.config?.url)) {
        localStorage.removeItem('token');
        window.location.href = '/admin/login';
      } else if (status === 401 && isCustomerRequest(error.config)) {
        // Customer 403s are usually checkout/payment validation
        // (e.g. token IP mismatch); don't blow away the session.
        localStorage.removeItem('customer_token');
      }
    }
    return Promise.reject(error);
  }
);

export default api;

